<template>
  <!-- 联系人概况：联系人结构与关键决策人 -->
  <div class="contact-overview">
    <list-page>
      <list-table>
        <div v-loading="loading" class="ov-body">
          <!-- KPI -->
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">联系人总数</div>
              <div class="value">{{ overview.contactCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">全部客户联系人</div>
            </div>
            <div class="stat-card">
              <div class="label">关键决策人</div>
              <div class="value warning">{{ overview.keyContactCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">标记为关键联系人的</div>
            </div>
            <div class="stat-card">
              <div class="label">本月新增</div>
              <div class="value">{{ overview.monthNewCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">{{ currentMonth }} 入库</div>
            </div>
          </div>

          <!-- 职位分布 / 增长趋势 -->
          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">职位分布</div>
              <div ref="positionChart" class="hbar-div"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">联系人增长趋势</div>
              <div ref="trendChart" class="chart-div"></div>
            </div>
          </div>

          <!-- 关键决策人 TOP10 -->
          <div class="chart-card">
            <div class="section-title">关键决策人 TOP10</div>
            <el-table :data="overview.keyContactList || []" border size="small" empty-text="暂无数据">
              <el-table-column label="姓名" prop="merchantRemarkName" min-width="140"/>
              <el-table-column label="所属客户" prop="customerName" min-width="200" show-overflow-tooltip>
                <template v-slot="{row}">{{ row.customerName || '—' }}</template>
              </el-table-column>
              <el-table-column label="职位" prop="customerPosition" width="140">
                <template v-slot="{row}">{{ dictName(positionList, row.customerPosition) || '—' }}</template>
              </el-table-column>
              <el-table-column label="手机号" prop="merchantRemarkPhone" width="160">
                <template v-slot="{row}">{{ row.merchantRemarkPhone || '—' }}</template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import contactApi from "@/api/dz-crm/partner/contactApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_POSITION, dictName} from "@/views/h5/crm/crmDictionary";
import {toChartRows} from "@/views/h5/crm/customerStat";
import {useRoute, useRouter} from "vue-router";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ContactOverview",
  components: {ListPage, ListTable},
  data() {
    return {
      loading: false,
      overview: {},
      positionChart: null,
      trendChart: null,
      positionList: [],
      dictReady: false,
      dataReady: false,
    };
  },
  computed: {
    currentMonth() {
      const now = new Date();
      return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    },
    positionRows() {
      return toChartRows(this.overview.positionStats, this.positionList, null, "contactCount");
    },
  },
  beforeRouteLeave(to, from, next) {
    // 只读的统计页，不缓存页面数据
    next();
  },
  mounted() {
    // 字典与数据都到齐才画图，否则职位还没解析出名字就画成了空白项
    commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_POSITION).then(list => {
      this.positionList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadOverview();
  },
  unmounted() {
    [this.positionChart, this.trendChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    dictName,
    async loadOverview() {
      this.loading = true;
      try {
        this.overview = await contactApi.overview({}) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      this.positionChart = this.renderHBar(this.positionChart, this.$refs.positionChart, this.positionRows);
      this.trendChart = this.renderLine(this.trendChart, this.$refs.trendChart,
        this.overview.trendMonthList || [], this.overview.trendCountList || []);
    },
    /** 横向条形图：职位分布，大的排在上面 */
    renderHBar(chart, dom, rows) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      // y 轴类目从下往上排，倒序后最大值才落在顶部
      const ordered = rows.slice().reverse();
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "36", bottom: "3%", top: "3%", containLabel: true},
        xAxis: {type: "value", minInterval: 1, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        yAxis: {
          type: "category",
          data: ordered.map(row => row.name),
          axisTick: {show: false},
          axisLabel: {color: "#909399"},
        },
        series: [{
          type: "bar",
          barWidth: "50%",
          itemStyle: {color: "#13b8b8", borderRadius: [0, 3, 3, 0]},
          label: {show: true, position: "right", color: "#606266", fontSize: 11},
          data: ordered.map(row => row.value),
        }],
      });
      return chart;
    },
    renderLine(chart, dom, xNames, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", boundaryGap: false, data: xNames, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", minInterval: 1, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "line", smooth: true, itemStyle: {color: "#13b8b8"}, areaStyle: {opacity: 0.08}, data}],
      });
      return chart;
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-overview {
  height: 100%;

  .ov-body {
    padding: 10px 0;
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 12px;
  }

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 14px 16px;
    background: #fff;

    .label {
      font-size: 13px;
      color: #909399;
    }

    .value {
      margin-top: 6px;
      font-size: 26px;
      font-weight: 600;
      color: #1f2329;
      line-height: 1.2;

      &.warning {
        color: #e6a23c;
      }

      .unit {
        margin-left: 4px;
        font-size: 13px;
        font-weight: 400;
        color: #909399;
      }
    }

    .sub {
      margin-top: 4px;
      font-size: 12px;
      color: #c0c4cc;
    }
  }

  .grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
    gap: 12px;
  }

  .chart-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 12px;
    background: #fff;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
    }
  }

  .chart-div {
    height: 280px;
  }

  .hbar-div {
    height: 280px;
  }
}
</style>
